import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Building2, User, MapPin, Phone, Mail, Calendar, AlertTriangle, CheckCircle, Eye, Edit, Store, Search } from 'lucide-react'
import { api } from '../../lib/api'
import AdminNavHeader from '../../components/AdminNavHeader'
import { getCache, getCacheStale, setCache } from '../../lib/cache'

interface StoreOwner {
  id: string
  name: string
  email: string
  phone: string
  is_active: boolean
  created_at: string
  store: {
    id: string
    name: string
    status: string
    is_active: boolean
    address: string
    phone: string
    email: string
  } | null
}

const AdminStoreOwners = () => {
  const navigate = useNavigate()
  const [storeOwners, setStoreOwners] = useState<StoreOwner[]>([])
  const [filteredOwners, setFilteredOwners] = useState<StoreOwner[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all')
  const [storeFilter, setStoreFilter] = useState<'all' | 'with_store' | 'without_store'>('all')

  useEffect(() => {
    loadStoreOwners()
  }, [])

  useEffect(() => {
    filterStoreOwners()
  }, [storeOwners, searchTerm, statusFilter, storeFilter])

  const loadStoreOwners = async () => {
    const cacheKey = 'admin_store_owners'
    
    // Intentar cargar desde cache primero
    const cachedOwners = getCacheStale<StoreOwner[]>(cacheKey)
    if (cachedOwners && cachedOwners.length > 0) {
      setStoreOwners(cachedOwners)
      setLoading(false)
    }

    try {
      setLoading(true)
      const response = await api.get('/admin/store-owners')
      const ownersData = response.data.data || []
      setStoreOwners(ownersData)
      
      // Guardar en cache (5 minutos de TTL)
      setCache(cacheKey, ownersData, 5 * 60 * 1000)
    } catch (error: any) {
      console.error('Error loading store owners:', error)
      
      // Si hay error de red y tenemos datos en cache, mantenerlos
      if ((error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED' || !error.response) && cachedOwners) {
        // Ya tenemos los datos del cache, no hacer nada más
        return
      }
      
      setStoreOwners([])
    } finally {
      setLoading(false)
    }
  }

  const handleStatusChange = async (userId: string, isActive: boolean) => {
    try {
      await api.patch(`/admin/users/${userId}/status`, { isActive })
      setStoreOwners(storeOwners.map(owner =>
        owner.id === userId ? { ...owner, is_active: isActive } : owner
      ))
    } catch (error) {
      console.error('Error updating store owner status:', error)
    }
  }

  const handleViewDetails = (owner: StoreOwner) => {
    navigate(`/admin/store-owners/${owner.id}`)
  }

  const filterStoreOwners = () => {
    let filtered = [...storeOwners]

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(owner =>
        owner.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        owner.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        owner.phone.includes(searchTerm)
      )
    }

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(owner =>
        statusFilter === 'active' ? owner.is_active : !owner.is_active
      )
    }

    // Filter by store presence
    if (storeFilter !== 'all') {
      filtered = filtered.filter(owner =>
        storeFilter === 'with_store' ? owner.store : !owner.store
      )
    }

    setFilteredOwners(filtered)
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando dueños de colmado...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      {/* Header */}
      <AdminNavHeader
        title="Dueños de Colmado"
        subtitle={`${filteredOwners.length} dueños registrados`}
      />

      {/* Search and Filters - iOS Style */}
      <div className="bg-white/70 backdrop-blur-xl border-b border-white/30 px-4 py-3">
        {/* Search Bar */}
        <div className="mb-3">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="w-4 h-4 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Buscar dueños..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-gray-50/80 border border-gray-200/50 rounded-2xl focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500/50 focus:bg-white transition-all duration-200 text-sm placeholder-gray-400"
            />
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-2 overflow-x-auto pb-1">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as 'all' | 'active' | 'inactive')}
            className="flex-shrink-0 px-4 py-2 bg-white/80 border border-gray-200/50 rounded-xl text-sm font-medium text-gray-700 focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500/50 transition-all duration-200"
          >
            <option value="all">Todos los estados</option>
            <option value="active">Activos</option>
            <option value="inactive">Inactivos</option>
          </select>

          <select
            value={storeFilter}
            onChange={(e) => setStoreFilter(e.target.value as 'all' | 'with_store' | 'without_store')}
            className="flex-shrink-0 px-4 py-2 bg-white/80 border border-gray-200/50 rounded-xl text-sm font-medium text-gray-700 focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500/50 transition-all duration-200"
          >
            <option value="all">Todos los colmados</option>
            <option value="with_store">Con colmado</option>
            <option value="without_store">Sin colmado</option>
          </select>

          {(searchTerm || statusFilter !== 'all' || storeFilter !== 'all') && (
            <button
              onClick={() => {
                setSearchTerm('')
                setStatusFilter('all')
                setStoreFilter('all')
              }}
              className="flex-shrink-0 px-4 py-2 bg-primary-500 text-white rounded-xl text-sm font-medium hover:bg-primary-600 active:bg-primary-700 transition-all duration-200"
            >
              Limpiar
            </button>
          )}
        </div>

        {/* Results count */}
        <div className="mt-3 text-xs text-gray-500 text-center">
          {filteredOwners.length} {filteredOwners.length === 1 ? 'dueño encontrado' : 'dueños encontrados'}
        </div>
      </div>

      {/* Content - iOS Style */}
      <div className="px-4 py-4 safe-area-bottom">
        <div className="space-y-3">
          {filteredOwners.map((owner) => (
            <div key={owner.id} className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-white/50 overflow-hidden active:scale-[0.98] transition-transform duration-150">
              {/* Header - iOS Style */}
              <div className="p-4 border-b border-gray-100/50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl flex items-center justify-center shadow-sm">
                      <User className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-semibold text-gray-900 truncate">{owner.name}</h3>
                      <p className="text-xs text-gray-500">Dueño de Colmado</p>
                    </div>
                  </div>
                  <div className="flex flex-col items-end space-y-1">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      owner.is_active
                        ? 'bg-green-100 text-green-700'
                        : 'bg-red-100 text-red-700'
                    }`}>
                      {owner.is_active ? 'Activo' : 'Inactivo'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Content - iOS Style */}
              <div className="p-4 space-y-3">
                {/* Contact Info */}
                <div className="space-y-2.5">
                  <div className="flex items-center text-sm">
                    <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                      <Mail className="w-4 h-4 text-blue-600" />
                    </div>
                    <span className="text-gray-700 truncate flex-1">{owner.email}</span>
                  </div>
                  <div className="flex items-center text-sm">
                    <div className="w-8 h-8 bg-green-50 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                      <Phone className="w-4 h-4 text-green-600" />
                    </div>
                    <span className="text-gray-700">{owner.phone}</span>
                  </div>
                  <div className="flex items-center text-sm">
                    <div className="w-8 h-8 bg-purple-50 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                      <Calendar className="w-4 h-4 text-purple-600" />
                    </div>
                    <span className="text-gray-700 text-xs">
                      Registrado {new Date(owner.created_at).toLocaleDateString('es-DO', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </span>
                  </div>
                </div>

                {/* Store Info - iOS Style */}
                {owner.store ? (
                  <div className="bg-gray-50/50 rounded-xl p-3 border border-gray-100/50">
                    <div className="flex items-center mb-2">
                      <div className="w-6 h-6 bg-orange-50 rounded-lg flex items-center justify-center mr-2">
                        <Building2 className="w-3.5 h-3.5 text-orange-600" />
                      </div>
                      <h4 className="text-sm font-semibold text-gray-900">Colmado Registrado</h4>
                    </div>
                    <div className="space-y-2 ml-8">
                      <p className="text-sm font-medium text-gray-900">{owner.store.name}</p>
                      <div className="flex items-start text-xs text-gray-600">
                        <MapPin className="w-3.5 h-3.5 mr-2 mt-0.5 flex-shrink-0 text-gray-400" />
                        <span className="leading-tight">{owner.store.address}</span>
                      </div>
                      <div className="flex items-center justify-between pt-1">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          owner.store.status === 'approved'
                            ? 'bg-green-100 text-green-700'
                            : owner.store.status === 'pending'
                            ? 'bg-yellow-100 text-yellow-700'
                            : 'bg-red-100 text-red-700'
                        }`}>
                          {owner.store.status === 'approved' ? 'Aprobado' :
                           owner.store.status === 'pending' ? 'Pendiente' : 'Rechazado'}
                        </span>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          owner.store.is_active
                            ? 'bg-green-100 text-green-700'
                            : 'bg-red-100 text-red-700'
                        }`}>
                          {owner.store.is_active ? 'Activo' : 'Inactivo'}
                        </span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-amber-50/80 rounded-xl p-3 border border-amber-200/50">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center mr-3">
                        <AlertTriangle className="w-4 h-4 text-amber-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-amber-800">Sin Colmado</p>
                        <p className="text-xs text-amber-700">No tiene colmado registrado aún</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Actions - iOS Style */}
                <div className="flex items-center justify-between pt-3">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleViewDetails(owner)}
                      className="flex items-center px-3 py-2 bg-gray-50 hover:bg-gray-100 rounded-xl text-xs font-medium text-gray-700 transition-colors active:scale-95"
                    >
                      <Eye className="w-3.5 h-3.5 mr-1.5" />
                      Ver detalles
                    </button>
                    <button
                      onClick={() => navigate(`/admin/store-owners/${owner.id}/edit`)}
                      className="flex items-center px-3 py-2 bg-blue-50 hover:bg-blue-100 rounded-xl text-xs font-medium text-blue-700 transition-colors active:scale-95"
                    >
                      <Edit className="w-3.5 h-3.5 mr-1.5" />
                      Editar
                    </button>
                  </div>

                  {owner.is_active ? (
                    <button
                      onClick={() => handleStatusChange(owner.id, false)}
                      className="flex items-center px-3 py-2 bg-red-50 hover:bg-red-100 rounded-xl text-xs font-medium text-red-700 transition-colors active:scale-95"
                    >
                      <AlertTriangle className="w-3.5 h-3.5 mr-1.5" />
                      Suspender
                    </button>
                  ) : (
                    <button
                      onClick={() => handleStatusChange(owner.id, true)}
                      className="flex items-center px-3 py-2 bg-green-50 hover:bg-green-100 rounded-xl text-xs font-medium text-green-700 transition-colors active:scale-95"
                    >
                      <CheckCircle className="w-3.5 h-3.5 mr-1.5" />
                      Activar
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredOwners.length === 0 && (
          <div className="text-center py-12">
            <Building2 className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No hay dueños de colmado</h3>
            <p className="mt-1 text-sm text-gray-500">
              Los usuarios con solicitudes aprobadas aparecerán aquí.
            </p>
          </div>
        )}

        {/* Stats - iOS Style */}
        <div className="grid grid-cols-2 gap-3 mt-6">
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-white/50">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center mr-3">
                <Building2 className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Total</p>
                <p className="text-xl font-bold text-gray-900">{filteredOwners.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-white/50">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-green-50 rounded-xl flex items-center justify-center mr-3">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Activos</p>
                <p className="text-xl font-bold text-gray-900">
                  {(filteredOwners || []).filter(o => o.is_active).length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-white/50">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-purple-50 rounded-xl flex items-center justify-center mr-3">
                <Store className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Con Colmado</p>
                <p className="text-xl font-bold text-gray-900">
                  {(filteredOwners || []).filter(o => o.store).length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-white/50">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-amber-50 rounded-xl flex items-center justify-center mr-3">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Sin Colmado</p>
                <p className="text-xl font-bold text-gray-900">
                  {(filteredOwners || []).filter(o => !o.store).length}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  )
}

export default AdminStoreOwners
